import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogIn, UserPlus, Mail, Lock, User, Building2, Briefcase, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { registerUser, loginUser, startGoogleLogin } from "@/lib/rexxponsApi";

function formatError(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

const Field = ({ icon: Icon, testid, ...props }) => (
  <div className="relative">
    <Icon strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-secondary/60 border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors"
    />
  </div>
);

export const AuthModal = ({ open, onClose }) => {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", role: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    if (busy) return;
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let data;
      if (mode === "register") {
        data = await registerUser({
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim(),
          company: form.company.trim(),
          role: form.role.trim(),
        });
        toast.success(`Welcome to Rexxpons, ${data.name || "there"}.`);
      } else {
        data = await loginUser(form.email.trim(), form.password);
        toast.success("Signed in.");
      }
      setUser(data);
      setForm({ email: "", password: "", name: "", company: "", role: "" });
      onClose();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail, "Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const isRegister = mode === "register";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="auth-modal"
          className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center overflow-y-auto p-4 py-10 bg-black/70 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            className="relative w-full max-w-md bg-card border border-border rounded-2xl p-7 shadow-2xl"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="auth-close-btn"
              onClick={close}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X strokeWidth={1.5} className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 border border-primary/30">
                {isRegister ? <UserPlus strokeWidth={1.5} className="w-4 h-4 text-primary" /> : <LogIn strokeWidth={1.5} className="w-4 h-4 text-primary" />}
              </span>
              <h2 className="font-heading text-2xl font-medium tracking-tight">
                {isRegister ? "Create your account" : "Welcome back"}
              </h2>
            </div>

            <div className="flex gap-1 p-1 bg-secondary/60 rounded-lg mb-6">
              <button
                data-testid="auth-tab-login"
                onClick={() => setMode("login")}
                className={`flex-1 text-xs tracking-[0.1em] uppercase py-2 rounded-md transition-colors ${!isRegister ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Sign in
              </button>
              <button
                data-testid="auth-tab-register"
                onClick={() => setMode("register")}
                className={`flex-1 text-xs tracking-[0.1em] uppercase py-2 rounded-md transition-colors ${isRegister ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Create account
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3.5">
              {isRegister && (
                <Field icon={User} testid="auth-name-input" value={form.name} onChange={set("name")} placeholder="Full name" autoComplete="name" />
              )}
              <Field icon={Mail} testid="auth-email-input" value={form.email} onChange={set("email")} type="email" placeholder="Email address" autoComplete="email" />
              <Field icon={Lock} testid="auth-password-input" value={form.password} onChange={set("password")} type="password" placeholder={isRegister ? "Create a password (min. 8 characters)" : "Password"} autoComplete={isRegister ? "new-password" : "current-password"} />
              {isRegister && (
                <>
                  <Field icon={Building2} testid="auth-company-input" value={form.company} onChange={set("company")} placeholder="Company / Organization (optional)" autoComplete="organization" />
                  <Field icon={Briefcase} testid="auth-role-input" value={form.role} onChange={set("role")} placeholder="Role / Profession (optional)" autoComplete="organization-title" />
                </>
              )}
              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm tracking-[0.08em] uppercase rounded-lg px-4 py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isRegister ? <UserPlus strokeWidth={1.8} className="w-4 h-4" /> : <LogIn strokeWidth={1.8} className="w-4 h-4" />}
                {busy ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <span className="flex-1 h-px bg-border" />
              <span className="text-[10px] tracking-[0.16em] uppercase text-muted-foreground">or</span>
              <span className="flex-1 h-px bg-border" />
            </div>

            <button
              data-testid="auth-google-btn"
              onClick={startGoogleLogin}
              className="w-full flex items-center justify-center gap-2.5 border border-border hover:border-primary/40 hover:bg-secondary transition-colors rounded-lg px-4 py-3 text-sm text-foreground"
            >
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
              Continue with Google
            </button>

            <p className="text-center text-xs text-muted-foreground/70 mt-5">
              {isRegister ? "By creating an account you agree to use Rexxpons responsibly." : "Your account keeps your Memory synced across devices."}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
